import React, { useState } from 'react'
import {
    getMultiFactorResolver,
    MultiFactorError,
    sendPasswordResetEmail,
    signInWithEmailAndPassword,
    TotpMultiFactorGenerator,
} from 'firebase/auth'
import { FirebaseError } from 'firebase/app'
import { auth } from '../../firebaseConfig'
import MFATOTPSetupStep from './MFATOTPSetupStep'
import MFACodeStep from './MFACodeStep'
import { Box, Button, Stack, TextField, Typography } from '@mui/material'
import ReferToEmailStep from './ReferToEmailStep'
import { Trans, useTranslation } from 'react-i18next'
import axios from 'axios'
import { getPlatformApiBase } from '../../utils/apiBase'
import { useSignInAttemptGuard } from '../../hooks/useSignInAttemptGuard'

interface CanResetPasswordResponse {
    allowed: boolean
}

const PLATFORM_API_BASE = getPlatformApiBase()

export interface PasswordStepProps {
    email: string
}

const PasswordStep: React.FC<PasswordStepProps> = ({ email }) => {
    const { t } = useTranslation()
    const [password, setPassword] = useState('')
    const [error, setError] = useState<string>('')
    const [nextStep, setNextStep] = useState<JSX.Element | null>(null)
    const [failedAttempts, setFailedAttempts] = useState(0)
    const { isPending: isSubmitting, start, cancel } = useSignInAttemptGuard()

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPassword(e.target.value)
        if (error) setError('') // Clear error when user types
    }

    const handleSubmit = async () => {
        // start() itself guards re-entry — including Enter-key resubmission, which doesn't go
        // through the button's own `disabled` — across every interactive sign-in entry point,
        // not just this one (see useSignInAttemptGuard).
        const attempt = start()
        if (attempt === null) return
        let mfaError: MultiFactorError | null = null
        try {
            console.info('[PasswordStep] Trying to sign in for:', email)
            const userCredential = await signInWithEmailAndPassword(auth, email, password)
            const user = userCredential.user

            if (!user.emailVerified) {
                console.info('[PasswordStep] Email is not verified.')
                cancel(attempt)
                setNextStep(
                    <Box>
                        <Stack spacing={2}>
                            <Typography color="info">
                                <Trans i18nKey="login.password_step.welcome_verify_email" />
                            </Typography>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={async () => {
                                    try {
                                        await sendPasswordResetEmail(auth, email)
                                        console.info('Password reset email sent successfully.')
                                        setNextStep(<ReferToEmailStep email={email} />)
                                    } catch (error) {
                                        console.error('Failed to send password reset email:', error)
                                        setError(t('login.password_step.error_send_email'))
                                    }
                                }}
                            >
                                {t('login.password_step.send_email_button')}
                            </Button>
                        </Stack>
                    </Box>
                )
                return
            }

            console.info('[PasswordStep] User has been signed in successfully')
            setNextStep(
                <MFATOTPSetupStep
                    email={email}
                    onNeedsReauth={() => {
                        // The only path back to this component's own form — nothing came of
                        // this attempt, so release it the same as a definite failure, or a
                        // retry here would find start() still blocked.
                        cancel(attempt)
                        setNextStep(null)
                    }}
                />
            )
        } catch (error: unknown) {
            console.warn('Failed to sign in user:', error)
            if (error instanceof FirebaseError && error.code === 'auth/multi-factor-auth-required') {
                mfaError = error as MultiFactorError
            } else {
                setError(t('login.password_step.error_invalid_password'))
                setPassword('')
                setFailedAttempts((prev) => prev + 1)
                cancel(attempt)
            }
        }

        if (!mfaError) return
        console.info('2FA required')

        try {
            const mfaResolver = getMultiFactorResolver(auth, mfaError)
            const totpFactor = mfaResolver.hints.find((hint) => hint.factorId === TotpMultiFactorGenerator.FACTOR_ID)
            if (totpFactor) {
                console.info('TOTP found, switching to MFA code step')
                setNextStep(<MFACodeStep mfaResolver={mfaResolver} enrollmentId={totpFactor.uid} />)
            } else {
                throw new Error('Unsupported MFA method detected. Only TOTP is currently supported.')
            }
        } catch (resolverError) {
            console.error('[PasswordStep] Failed to get MFA resolver:', resolverError)
            setError(t('login.password_step.error_mfa_verify'))
            cancel(attempt)
        }
    }

    const handleForgotPassword = async () => {
        let allowed: boolean
        try {
            const { data } = await axios.post<CanResetPasswordResponse>(
                '/auth/can-reset-password',
                { email },
                { baseURL: PLATFORM_API_BASE }
            )
            allowed = data.allowed
        } catch {
            setError(t('login.password_step.error_send_email'))
            return
        }

        if (!allowed) {
            setError(t('login.password_step.sso_hint'))
            return
        }

        try {
            await sendPasswordResetEmail(auth, email)
            setError(t('login.password_step.reset_email_sent'))
            setNextStep(<ReferToEmailStep email={email} />)
        } catch {
            setError(t('login.password_step.error_send_reset_email'))
        }
    }

    const handleKeyPress = async (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            await handleSubmit()
        }
    }

    if (nextStep) return nextStep

    return (
        <Box>
            <Stack>
                <Typography variant="h6" align="center">
                    {t('login.password_step.title')}
                </Typography>

                <TextField
                    type="password"
                    // eslint-disable-next-line jsx-a11y/no-autofocus -- first field of a fresh step, nothing else to focus instead
                    autoFocus
                    label={t('login.password_step.password_label')}
                    variant="outlined"
                    value={password}
                    onChange={handleChange}
                    onKeyDown={handleKeyPress}
                    fullWidth
                />

                {error && <Typography color="error">{error}</Typography>}

                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    disabled={!password || !!error || isSubmitting}
                >
                    {t('login.password_step.login_button')}
                </Button>

                {failedAttempts >= 1 && (
                    <Button onClick={handleForgotPassword} color="error" sx={{ textTransform: 'none' }}>
                        {t('login.password_step.forgot_password_button')}
                    </Button>
                )}
            </Stack>
        </Box>
    )
}

export default PasswordStep
